/*
 * How a suggestion read off a document lands in a form (task 38.03 §7),
 * keyed by the `extract` value on the documentNavigator config.
 *
 * Every map mirrors the form's own pickers, so an applied value is the same
 * value picking it by hand would have written:
 *   supplier.valueMap   = the supplierId connect field's valueMap
 *   lines.account       = the expenseAccountId category picker's valueMap
 *   lines.tax           = the itemTaxId VAT select's valueMap
 *   vatTax.params       = that VAT select's entityParams (form field per key)
 *   currency            = the currency picker's valueMap
 * Change one of those fields in cardViewData and change it here too.
 */
export const extractionData = {
    expense: {
        // Header values: extracted key → form field. Apply replaces them.
        header: {
            date: 'date',
            supplierInvoiceNumber: 'supplierInvoiceNumber',
            dueDate: 'paymentDate'
        },
        // Date values are whole days; the form stores ISO date-times.
        dates: ['date', 'dueDate'],
        currency: { id: 'currency', name: 'currencyName', collection: 'currency', base: 'baseCurrency', rate: 'exchangeRate' },
        // Fields whose fieldsLogic must run after Apply, as if typed.
        fieldsLogic: ['date', 'currency'],
        supplier: {
            entity: 'contact',
            idField: 'supplierId',
            valueMap: {
                'supplierId': 'id',
                'supplierName': 'name',
                'supplierEmail': 'email',
                'supplierAddress_street': 'address_street',
                'supplierAddress_postCode': 'address_postCode',
                'supplierAddress_city': 'address_city',
                'supplierAddress_region': 'address_region',
                'supplierCountry': 'country',
                'supplierCountryName': 'countryName',
                'companyTypeId': 'companyTypeId'
            }
        },
        // A supplier created from the document: contact field → extracted supplier key.
        newSupplier: {
            entity: 'contact',
            defaults: { contactType: 'supplier', companyTypeId: 'company' },
            map: {
                name: 'name',
                email: 'email',
                taxId: 'taxId',
                address_street: 'street',
                address_postCode: 'postCode',
                address_city: 'city',
                country: 'country'
            },
            // What the national registry (company-info) fills in when the
            // document leaves it empty: contact field → CompanyInfo path.
            registry: {
                name: 'name',
                address_street: 'address.street',
                address_postCode: 'address.postalCode',
                address_city: 'address.city'
            }
        },
        lines: {
            array: 'lines',
            title: 'itemName',
            description: 'itemDescription',
            quantity: 'itemQuantity',
            gross: 'enteredGrossAmount',
            itemType: { field: 'itemType', value: 'purchased' },
            account: { id: 'expenseAccountId', name: 'expenseAccountIdName', number: 'expenseAccountNumber' },
            tax: { id: 'itemTaxId', name: 'itemTaxName', rate: 'itemTaxRate' }
        },
        // Apply marks the document's attachment, so a reopened record does not
        // offer the suggestion again (the attachment whose `key` is the file id).
        appliedMark: { array: 'attachments', key: 'fileUrl', field: 'suggestionAppliedAt' },
        vatTax: {
            entity: 'vatTax',
            params: {
                // The org's own jurisdiction. Select adds it to every lookup;
                // without it GET vatTax answers with the 'WW' list, which has
                // no 25 % code at all.
                country: { or: ['org.country'] },
                scope: { value: 'purchases' },
                // The country rules need a country: the supplier's, else the
                // one printed on the document, else the org's (domestic).
                recipientLocation: { field: 'supplierCountry', or: ['document.supplier.country', 'org.country'] },
                companyType: { field: 'companyTypeId' },
                productType: { field: 'itemType' }
            }
        },
        /*
         * How the panel looks (ExtractionPanel). The display sections are the
         * card's own display format (DisplayColumns), read against the
         * extracted document — the header block — and against each extracted
         * line. The pickers are CardOptions: `count` cards show before
         * "+N more". A `required` picker is marked "Choose one" until something
         * is chosen — what the expense needs: a supplier (Save refuses without
         * one) and an account per line (its category in the books). The VAT
         * code is not: left open, Apply takes the org's rule default.
         */
        panel: {
            title: 'document.extract.title',
            onMount: 'expand',
            // After Apply every section and line of the form closes to its display.
            collapseFormOnApply: true,
            displaySection: {
                columns: [
                    {
                        weight: 1, basis: '60%',
                        elements: [
                            { type: 'field', value: 'supplier.name', scale: 'title' },
                            { type: 'date', value: 'date', scale: 'normal' },
                            { type: 'field', value: 'supplierInvoiceNumber', prefix: 'document.extract.invoiceNumber', scale: 'normal', condition: { field: 'supplierInvoiceNumber', operator: 'notEmpty' } }
                        ]
                    },
                    {
                        weight: 1, basis: '40%', align: 'end',
                        elements: [
                            { type: 'amount', value: 'totals.gross', decorator: 'currency', scale: 'title' },
                            { type: 'merge', value: 'document.extract.lineCount', scale: 'normal', mergeParams: ['lineCount'] }
                        ]
                    }
                ]
            },
            supplier: { label: 'document.extract.supplier', count: 3, required: true },
            lines: {
                displaySection: {
                    columns: [
                        {
                            weight: 1, basis: '70%',
                            elements: [
                                { type: 'field', value: 'text', scale: 'normal' }
                            ]
                        },
                        {
                            weight: 1, basis: '30%', align: 'end',
                            elements: [
                                { type: 'amount', value: 'gross', decorator: 'currency', scale: 'normal' }
                            ]
                        }
                    ]
                },
                title: { label: 'document.extract.lineTitle', count: 3 },
                account: { label: 'document.extract.account', count: 3, required: true },
                vat: { label: 'document.extract.vat', count: 3 }
            },
            buttons: [
                { type: 'button', id: 'documentSuggestion.apply', action: 'apply', label: 'document.extract.apply', variant: 'primary' }
            ]
        }
    }
}
